/**
 * HUD tuning (plan G10): marker sizes, fade times, edge margins and weapon labels. The values live in hud.json;
 * the parsed object is mutable so the dev panel (G14) can bind it live (the model and view read it every frame,
 * except `maxContacts`, which sizes the contact-marker pool once at init).
 */
import { z } from 'zod'
import { weaponIdSchema } from '../../data/schemas'
import hudJson from './hud.json'

const positive = z.number().positive()
const nonNegative = z.number().nonnegative()

/** Exhaustive over the weapon ids (Zod 4 enum-keyed records require every key). */
const weaponLabels = z.record(weaponIdSchema, z.string().min(1).max(8))

export const hudTuningSchema = z.object({
  /** Inset (CSS px) of the off-screen target arrow from the viewport edge. */
  edgeMarginPx: nonNegative,
  /** The boresight (gun cross) is drawn this far along the nose when there is no target (m). */
  aimDistance: positive,
  /** With a target, the boresight sits at the target's distance, but never nearer than this (m). */
  boresightMinDistance: positive,
  bracket: z.object({
    /** Smallest and largest bracket half-size (CSS px). */
    minHalfPx: positive,
    maxHalfPx: positive,
    /** Bracket half-size = ship collider radius × this × px/m + pad. */
    radiusScale: positive,
    padPx: nonNegative,
  }),
  /** The lead pip shows while the target is within the selected weapon's range × this. */
  leadRangeFactor: positive,
  /** Below this speed (m/s) the velocity-vector marker hides. */
  velocityMinSpeed: nonNegative,
  /** Hit markers show for this long after our hit (spec: 0.25 s). */
  hitMarkerSeconds: positive,
  wedge: z.object({
    /** Damage-direction wedges fade out over this many seconds (spec: 1 s). */
    fadeSeconds: positive,
    /** Hits within this angle of a live wedge refresh it instead of taking a new one. */
    mergeDeg: z.number().min(0).max(180),
    /** Wedge ring radius around the screen centre (CSS px). */
    radiusPx: positive,
  }),
  /** The "WAVE n" banner shows this long after a wave starts. */
  waveBannerSeconds: nonNegative,
  /** Hull fraction below which the hull bar turns to its warning state. */
  lowHullFraction: z.number().min(0).max(1),
  /** Contact markers (every other visible ship) in the pool; sized once at init. */
  maxContacts: z.number().int().min(0).max(64),
  /** Short weapon names on the weapon readout. */
  weaponLabels,
})

export type HudTuning = z.infer<typeof hudTuningSchema>

export function parseHudTuning(raw: unknown): HudTuning {
  return hudTuningSchema.parse(raw)
}

/** The live tuning, parsed once from hud.json. */
export const hudTuning: HudTuning = parseHudTuning(hudJson)
